import React, { useEffect, useState, useRef, useCallback } from "react";
import { Effect } from "../../types/effects";
import dreamWorldProvider from "../../DreamWorldProvider";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { DocumentSnapshot } from "firebase/firestore";
import { useSelector } from "react-redux";
import { selectUserGlobalData } from "../../store/dreamworldSlice";

interface AllRecentEffectsModalProps {
  onClose: () => void;
}

const AllRecentEffectsModal: React.FC<AllRecentEffectsModalProps> = ({
  onClose,
}) => {
  const [effects, setEffects] = useState<Effect[]>([]);
  const [lastVisible, setLastVisible] =
    useState<DocumentSnapshot<any> | null>(null);
  const [loading, setLoading] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const userGlobalData = useSelector(selectUserGlobalData);

  const fetchMoreEffects = useCallback(async () => {
    if (loading || !userGlobalData?.user?.uid) return;
    setLoading(true);
    const { effects: newEffects, lastVisible: newLastVisible } =
      await dreamWorldProvider.getRecentEffects(
        userGlobalData.user.uid,
        lastVisible
      );
    setEffects((prev) => [...prev, ...newEffects]);
    setLastVisible(newLastVisible);
    setLoading(false);
  }, [lastVisible, loading, userGlobalData]);

  useEffect(() => {
    fetchMoreEffects();
  }, []);

  const lastEffectElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && lastVisible) {
          fetchMoreEffects();
        }
      });
      if (node && observer.current) observer.current.observe(node);
    },
    [loading, fetchMoreEffects, lastVisible]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    >
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl h-3/4 flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Recent Effects</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {effects.map((effect, index) => {
              if (effects.length === index + 1) {
                return (
                  <div ref={lastEffectElementRef} key={effect.id}>
                    <EffectCard effect={effect} />
                  </div>
                );
              } else {
                return <EffectCard effect={effect} key={effect.id} />;
              }
            })}
          </div>
          {loading && <p className="text-white text-center mt-4">Loading...</p>}
        </div>
      </div>
    </motion.div>
  );
};

const EffectCard: React.FC<{ effect: Effect }> = ({ effect }) => (
  <motion.div
    className="bg-gray-800 rounded-lg shadow-lg"
    whileHover={{ scale: 1.05 }}
  >
    <img
      src={effect.image}
      alt={effect.title}
      className="w-full h-32 object-cover rounded-t-lg"
    />
    <div className="p-4">
      <h3 className="text-white font-semibold">{effect.title}</h3>
      <p className="text-gray-400 text-sm">{effect.description}</p>
      <p className="text-purple-400 text-xs mt-2">
        Used {effect.useCount} times
      </p>
    </div>
  </motion.div>
);

export default AllRecentEffectsModal;
